import { FC, useEffect, useState } from "react";
import {
  millisToTimeRemaining,
  timeRemainingToMillis,
} from "@/services/dateTimeService";
import { TextField } from "@mui/material";

const DurationTextField: FC<{
  initialValueInMillis: number;
  onTimestampUpdate: (newTimestamp: number) => void;
  label: string;
}> = ({ initialValueInMillis, onTimestampUpdate, label }) => {
  const [timestampTextFieldInput, setTimestampTextFieldInput] = useState(
    formatDurationInput(millisToTimeRemaining(initialValueInMillis)),
  );

  useEffect(() => {
    setTimestampTextFieldInput(
      formatDurationInput(millisToTimeRemaining(initialValueInMillis)),
    );
  }, [initialValueInMillis]);

  const [showError, setShowError] = useState(false);

  return (
    <TextField
      id="outlined-basic"
      label={label}
      variant="outlined"
      error={showError}
      onChange={(event) => {
        let input = event.target.value;
        const formattedInput = formatDurationInput(input);
        setTimestampTextFieldInput(formattedInput);
        try {
          const timestamp = timeRemainingToMillis(formattedInput);
          setShowError(false);
          onTimestampUpdate(timestamp);
        } catch {
          setShowError(true);
          onTimestampUpdate(0);
        }
      }}
      value={timestampTextFieldInput}
    />
  );
};

const formatDurationInput = (value: string): string => {
  // 1. Get only digits
  const digits = Number.parseInt(value.replace(/\D/g, "")).toString();
  const len = digits.length;

  if (len === 0) return "";

  // 2. Apply formatting based on number of digits
  if (len === 1) return `0:00:0${digits}`; // "1" -> "0:00:01"
  if (len === 2) return `0:00:${digits}`; // "12" -> "0:00:12"
  if (len === 3) return `0:0${digits[0]}:${digits.slice(1)}`; // "123" -> "0:01:23"
  if (len === 4) return `0:${digits.slice(0, 2)}:${digits.slice(2)}`; // "1234" -> "0:12:34"
  if (len === 5) return `${digits[0]}:${digits.slice(1, 3)}:${digits.slice(3)}`; // "12345" -> "1:23:45"

  // For 6 or more digits, parse into HH:MM:SS
  const ss = digits.slice(-2);
  const mm = digits.slice(-4, -2);
  const hh = digits.slice(0, -4);
  return `${hh}:${mm}:${ss}`; // e.g., "12:34:56" or "123:45:67"
};

export default DurationTextField;
